import { useState, useEffect, useCallback } from 'react';
import { useMap, CircleMarker } from 'react-leaflet';
import { useQuery, useQueryClient } from 'react-query';
import { Plus, Droplets, RefreshCw, Map, List, LocateFixed } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { MapCanvas } from '@/components/maps/MapCanvas';
import { WaterSourceOverlay } from '@/components/maps/WaterSourceOverlay';
import { WaterSourceDetailDrawer } from '@/components/maps/WaterSourceDetailDrawer';
import { WaterSourceFormModal } from './WaterSourceFormModal';
import { listWaterSources } from '@/api/waterSources';
import { useAuth } from '@/contexts/AuthContext';
import { useTenant } from '@/contexts/TenantContext';
import { useFpaMapHome } from '@/hooks/useFpaMapHome';
import { haversineKm, formatDistance } from '@/utils/distance';
import type { WaterSourceRecord } from '@/components/maps/WaterSourceDetailDrawer';

const LOCATED_ZOOM = 14; // "max zoom (18) - 4"

const CONDITION_COLOR: Record<string, string> = {
  GOOD: 'bg-green-100 text-green-800 border-green-300',
  FAIR: 'bg-amber-100 text-amber-800 border-amber-300',
  POOR: 'bg-red-100 text-red-800 border-red-300',
  OUT_OF_SERVICE: 'bg-gray-100 text-gray-600 border-gray-300',
};

const CONDITION_LABEL: Record<string, string> = {
  GOOD: 'Good',
  FAIR: 'Fair',
  POOR: 'Poor',
  OUT_OF_SERVICE: 'Out of service',
};

/** Renders inside MapCanvas — flies to the user's position and shows a blue dot. */
function LocationLayer({ position }: { position: [number, number] | null }) {
  const map = useMap();

  useEffect(() => {
    if (position) {
      map.flyTo(position, LOCATED_ZOOM, { animate: true, duration: 1.2 });
    }
  }, [position, map]);

  if (!position) return null;

  return (
    <CircleMarker
      center={position}
      radius={9}
      pathOptions={{
        color: '#1565c0',
        fillColor: '#42a5f5',
        fillOpacity: 0.9,
        weight: 3,
      }}
    />
  );
}

export default function WaterSourcesPage() {
  const { currentUser, can } = useAuth();
  const canCreateWS = can('water_source', 'create');
  const canEditWS = can('water_source', 'update');
  const { tenantId: switcherTenantId } = useTenant();
  // Super-admins use the TopBar switcher; regular staff are pinned to their
  // own tenant from the auth token.
  const tenantId = switcherTenantId ?? currentUser?.tenant_id;
  const mapHome = useFpaMapHome(tenantId ?? null);
  const qc = useQueryClient();

  const [typeFilter, setTypeFilter] = useState<string>('ALL');
  const [conditionFilter, setConditionFilter] = useState<string>('ALL');
  const [formOpen, setFormOpen] = useState(false);
  const [editSource, setEditSource] = useState<WaterSourceRecord | null>(null);
  const [drawerSource, setDrawerSource] = useState<WaterSourceRecord | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [refreshKey, setRefreshKey] = useState(0);
  const [mobileView, setMobileView] = useState<'map' | 'list'>('map');
  const [userLocation, setUserLocation] = useState<[number, number] | null>(null);

  // Request geolocation once on mount.
  useEffect(() => {
    if (!navigator.geolocation) return;
    navigator.geolocation.getCurrentPosition(
      (pos) => setUserLocation([pos.coords.latitude, pos.coords.longitude]),
      () => {/* permission denied or error — stay on default center */},
      { timeout: 10_000, maximumAge: 60_000 },
    );
  }, []);

  const recenter = useCallback(() => {
    navigator.geolocation?.getCurrentPosition(
      (pos) => setUserLocation([pos.coords.latitude, pos.coords.longitude]),
    );
  }, []);

  const params: Record<string, string | number> = { tenant_id: tenantId! };
  if (typeFilter !== 'ALL') params.source_type = typeFilter;
  if (conditionFilter !== 'ALL') params.condition = conditionFilter;

  const { data, isLoading } = useQuery(
    ['water-sources', tenantId, typeFilter, conditionFilter],
    () => listWaterSources(params),
    { enabled: Boolean(tenantId) },
  );

  const sources: WaterSourceRecord[] = data?.water_sources ?? [];

  // Sort by distance when we have a location, otherwise leave API order.
  const sortedSources = userLocation
    ? [...sources].sort((a, b) => {
        const da = haversineKm(userLocation, [a.lat, a.lng]);
        const db = haversineKm(userLocation, [b.lat, b.lng]);
        return da - db;
      })
    : sources;

  function handleSaved(saved: WaterSourceRecord) {
    qc.invalidateQueries(['water-sources']);
    setRefreshKey((k) => k + 1);
    setFormOpen(false);
    setEditSource(null);
  }

  function handleSourceClick(source: WaterSourceRecord) {
    setDrawerSource(source);
    setDrawerOpen(true);
  }

  function handleEdit(source: WaterSourceRecord) {
    setEditSource(source);
    setFormOpen(true);
  }

  if (!tenantId) return null;

  const mapPanel = (
    <div className="overflow-hidden rounded-lg border border-border h-full min-h-[50vh]">
      <MapCanvas
        center={mapHome.center}
        zoom={mapHome.zoom}
        tenantId={tenantId}
        gearMode="full"
        mapSearch
        variant="lg"
        /* Open framed on the user's FPA rather than on the whole country.
           `LocationLayer` still wins once geolocation resolves — finding the
           nearest source to where you're standing is the more specific
           intent; this only sets the view before (or without) a GPS fix. */
        fitBoundsTo={mapHome.fitBoundsTo}
      >
        {() => (
          <>
            <LocationLayer position={userLocation} />
            <WaterSourceOverlay
              tenantId={tenantId}
              /* Gated: the overlay derives canEdit from whether onEdit was
                 passed, so handing it over unconditionally would show an
                 Edit button to a read-only role. */
              onEdit={canEditWS ? handleEdit : undefined}
              onSourceClick={handleSourceClick}
              refreshKey={refreshKey}
            />
          </>
        )}
      </MapCanvas>
    </div>
  );

  const tablePanel = (
    <div className="overflow-auto rounded-lg border border-border">
      {isLoading ? (
        <div className="flex h-32 items-center justify-center text-sm text-muted-foreground">
          Loading…
        </div>
      ) : sortedSources.length === 0 ? (
        <div className="flex h-32 flex-col items-center justify-center gap-2 text-sm text-muted-foreground">
          <Droplets className="h-8 w-8 opacity-30" />
          No water sources yet.
        </div>
      ) : (
        <table className="w-full text-sm">
          <thead className="border-b border-border bg-muted/40">
            <tr>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Name</th>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground hidden sm:table-cell">Type</th>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">Condition</th>
              <th className="px-3 py-2.5 text-left font-medium text-muted-foreground">
                {userLocation ? 'Distance' : 'Inspected'}
              </th>
              <th className="px-3 py-2.5" />
            </tr>
          </thead>
          <tbody>
            {sortedSources.map((s) => {
              const dist = userLocation ? haversineKm(userLocation, [s.lat, s.lng]) : null;
              return (
                <tr
                  key={s.id}
                  className="border-b border-border last:border-0 hover:bg-muted/30 cursor-pointer active:bg-muted/50"
                  onClick={() => handleSourceClick(s)}
                >
                  <td className="px-3 py-2.5 font-medium">{s.name}</td>
                  <td className="px-3 py-2.5 text-muted-foreground hidden sm:table-cell">
                    {s.source_type === 'HYDRANT' ? 'Hydrant' : 'Private'}
                  </td>
                  <td className="px-3 py-2.5">
                    <Badge
                      variant="outline"
                      className={`text-xs font-normal ${CONDITION_COLOR[s.condition] ?? ''}`}
                    >
                      {CONDITION_LABEL[s.condition] ?? s.condition}
                    </Badge>
                  </td>
                  <td className="px-3 py-2.5 text-muted-foreground text-xs">
                    {dist != null ? formatDistance(dist) : (s.last_inspected ?? '—')}
                  </td>
                  <td className="px-3 py-2.5 text-right">
                    {canEditWS && (
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-7 text-xs"
                        onClick={(e) => { e.stopPropagation(); handleEdit(s); }}
                      >
                        Edit
                      </Button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Droplets className="h-5 w-5 text-primary" />
          <h1 className="text-xl font-semibold">Water Sources</h1>
          {!isLoading && (
            <span className="text-sm text-muted-foreground">({data?.total ?? 0})</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Recenter button — visible when location is available */}
          {userLocation && (
            <Button size="sm" variant="outline" onClick={recenter} className="gap-1.5 hidden sm:flex">
              <LocateFixed className="h-4 w-4" />
              My location
            </Button>
          )}
          {canCreateWS && (
            <Button size="sm" onClick={() => { setEditSource(null); setFormOpen(true); }} className="gap-1.5">
              <Plus className="h-4 w-4" />
              Add source
            </Button>
          )}
        </div>
      </div>

      {/* Filters + mobile toggle row */}
      <div className="flex flex-wrap items-center gap-2">
        <Select value={typeFilter} onValueChange={setTypeFilter}>
          <SelectTrigger className="h-8 w-40 text-sm">
            <SelectValue placeholder="All types" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All types</SelectItem>
            <SelectItem value="HYDRANT">Hydrant</SelectItem>
            <SelectItem value="PRIVATE">Private</SelectItem>
          </SelectContent>
        </Select>
        <Select value={conditionFilter} onValueChange={setConditionFilter}>
          <SelectTrigger className="h-8 w-40 text-sm">
            <SelectValue placeholder="All conditions" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="ALL">All conditions</SelectItem>
            <SelectItem value="GOOD">Good</SelectItem>
            <SelectItem value="FAIR">Fair</SelectItem>
            <SelectItem value="POOR">Poor</SelectItem>
            <SelectItem value="OUT_OF_SERVICE">Out of service</SelectItem>
          </SelectContent>
        </Select>
        <Button
          variant="ghost"
          size="sm"
          className="h-8 gap-1 text-sm"
          onClick={() => { qc.invalidateQueries(['water-sources']); setRefreshKey((k) => k + 1); }}
        >
          <RefreshCw className="h-3.5 w-3.5" />
          Refresh
        </Button>

        {/* Mobile map/list toggle — only visible on small screens */}
        <div className="ml-auto flex rounded-md border border-border lg:hidden">
          <button
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-l-md transition-colors ${
              mobileView === 'map'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
            }`}
            onClick={() => setMobileView('map')}
          >
            <Map className="h-3.5 w-3.5" />
            Map
          </button>
          <button
            className={`flex items-center gap-1.5 px-3 py-1.5 text-sm rounded-r-md border-l border-border transition-colors ${
              mobileView === 'list'
                ? 'bg-primary text-primary-foreground'
                : 'text-muted-foreground hover:bg-muted'
            }`}
            onClick={() => setMobileView('list')}
          >
            <List className="h-3.5 w-3.5" />
            List
          </button>
        </div>
      </div>

      {/* Desktop: side-by-side. Mobile: single panel controlled by toggle. */}
      <div className="grid flex-1 gap-4 lg:grid-cols-2">
        {/* Map — always visible on desktop; on mobile only when toggle=map */}
        <div className={mobileView === 'list' ? 'hidden lg:block' : ''}>
          {mapPanel}
        </div>

        {/* Table — always visible on desktop; on mobile only when toggle=list */}
        <div className={mobileView === 'map' ? 'hidden lg:block' : ''}>
          {tablePanel}
        </div>
      </div>

      <WaterSourceDetailDrawer
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        source={drawerSource}
        canEdit={canEditWS}
        onEdit={(s) => { setDrawerOpen(false); handleEdit(s); }}
        onInspected={(updated) => {
          setDrawerSource(updated);
          qc.invalidateQueries(['water-sources']);
          setRefreshKey((k) => k + 1);
        }}
      />

      <WaterSourceFormModal
        open={formOpen}
        onOpenChange={(v) => { setFormOpen(v); if (!v) setEditSource(null); }}
        tenantId={tenantId}
        source={editSource}
        onSaved={handleSaved}
      />
    </div>
  );
}
